import { createContext, useContext, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

import { OptionTypeConstants } from "@/constants/constants";
import { Option_Toast_Message } from "@/constants/task-claims.constant";
import useGetFilter, { filterItem } from "@/hooks/useGetFilter";
import { NewTOptions, TOptions } from "@/interfaces/interfaces";
import { formatToCapitalizedWords, renderFullName } from "@/lib/utils";
import {
  claimsApi,
  useGetClaimsOptionsQuery,
  useMutateClaimOptionMutation,
  useMutateClaimsDetailsMutation,
  useMutateClaimsMutation,
} from "@/redux/api/claimsQuery";
import { useGetWorkforceUsersQuery } from "@/redux/api/generalQuery";
import {
  useMutateEstimateMutation,
  useMutateEstimateOptionsMutation,
} from "@/redux/api/prQuery";
import {
  AllForOptions,
  TAllOptions,
  TNewEstimateOptions,
} from "@/schemas/GlobalSchema";
import { validateResponse } from "@/utils/helper-functions/form.helper";

type TmutateClaims = {
  claimsId: string;
  claimsType: string;
  data?: object;
  showToast?: boolean;
  method: "PATCH" | "DELETE";
};
export type TupdateDetailData = {
  data: object;
  claimsId: string;
  claimsType: string;
  claimsDetailId: string;
  successMsg: string;
};

type TapprovedOption = {
  value: string;
  id: string;
  statusFor: "poStatus" | "claimStatus" | "invoiceMethod";
  claimsType: string;
  claimsDetailId?: string;
  successMsg: string;
};

type TaskClaimsContextProps = {
  option: {
    isOpen: boolean;
    statusFor: string;
  };
  setOption: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      statusFor: string;
    }>
  >;
  isSheet: boolean;
  setSheet: React.Dispatch<React.SetStateAction<boolean>>;
  deleteOption: (idx: string) => Promise<void>;
  addOption: (data: NewTOptions<AllForOptions>) => Promise<void>;
  editOption: (data: TNewEstimateOptions) => Promise<void>;
  poStatusList: TAllOptions[];
  invoiceMethodList: TAllOptions[];
  statuses: TAllOptions[];
  submissions: TAllOptions[];
  estimatedByList: TOptions[];
  customerList: filterItem[];

  optionMutate: (
    value: string,
    id: string,
    statusFor: "poStatus" | "claimStatus" | "invoiceMethod",
  ) => Promise<void>;
  updatePoNumber: (value: string, id: string) => Promise<void>;
  approvedOptionMutate: ({
    value,
    id,
    statusFor,
    claimsType,
    claimsDetailId,
  }: TapprovedOption) => Promise<void>;
  updateDetailData: ({
    data,
    claimsId,
    claimsType,
    claimsDetailId,
    successMsg,
  }: TupdateDetailData) => Promise<void>;

  mutateClaimsInvoice: ({
    claimsId,
    claimsType,
    data,
  }: TmutateClaims) => Promise<void>;

  handleEstimateMutation: (
    data: object,
    estimateId: string,
    msg: string,
  ) => Promise<void>;
  addEstimationOptions: (data: NewTOptions<AllForOptions>) => Promise<void>;
  editEstimationOptions: (data: TNewEstimateOptions) => Promise<void>;
};

const taskClaimsContext = createContext<TaskClaimsContextProps>(
  {} as TaskClaimsContextProps,
);

export const TaskClaimsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [option, setOption] = useState({
    isOpen: false,
    statusFor: "",
  });
  const [isSheet, setSheet] = useState(false);
  const dispatch = useDispatch();
  const [mutateClaimOption] = useMutateClaimOptionMutation();

  const { data: statusList = [] } = useGetClaimsOptionsQuery({});

  const claimsStatus = useMemo(() => {
    return {
      poStatusList: statusList?.filter(
        (status) => status.for === OptionTypeConstants.PO_STATUS,
      ),

      invoiceMethodList: statusList?.filter(
        (status) => status.for === OptionTypeConstants.INVOICE_METHOD,
      ),
      statuses: statusList?.filter(
        (status) => status.for === OptionTypeConstants.ESTIMATION_STATUS,
      ),
      submissions: statusList?.filter(
        (status) =>
          status.for === OptionTypeConstants.ESTIMATION_SUBMISSION_METHOD,
      ),
    };
  }, [statusList]);

  const { customerList } = useGetFilter(["customer"]);

  const { data: userList } = useGetWorkforceUsersQuery({
    roles: ["SUPERADMIN", "ADMIN", "MANAGER"],
  });

  const estimatedByList: TOptions[] = userList?.data?.length
    ? userList?.data?.map((l) => {
        return {
          id: l.id,
          label: renderFullName({ params: l }),
        };
      })
    : [];

  const deleteOption = async (idx: string): Promise<void> => {
    const res = await mutateClaimOption({ method: "DELETE", optionId: idx });
    validateResponse({
      response: res,
      onSuccess: () => {
        toast.success(Option_Toast_Message.DELETE);
      },
    });
  };
  const addOption = async (data: NewTOptions<AllForOptions>): Promise<void> => {
    const res = await mutateClaimOption({ method: "POST", data });
    validateResponse({
      response: res,
      onSuccess: () => {
        toast.success(Option_Toast_Message.ADD);
      },
    });
  };

  const editOption = async (data: TNewEstimateOptions): Promise<void> => {
    const dataToSend = data.data;
    const res = await mutateClaimOption({ data: dataToSend, method: "PUT" });
    validateResponse({
      response: res,
      onSuccess: () => {
        toast.success(Option_Toast_Message.UPDATE);
      },
    });
  };

  const [mutateClaims] = useMutateClaimsMutation();
  const optionMutate = async (
    value: string,
    id: string,
    statusFor: "poStatus" | "claimStatus" | "invoiceMethod",
  ): Promise<void> => {
    if (!id || value === "") {
      return;
    }

    const res = await mutateClaims({
      claimsId: id,
      method: "PUT",
      data: {
        [statusFor]: value,
      },
    });

    if (res.data) {
      toast.success(
        `${formatToCapitalizedWords(statusFor)} Updated Successfully`,
      );
    }
  };

  const updatePoNumber = async (value: string, id: string): Promise<void> => {
    if (!value || !id) {
      return;
    }
    const res = await mutateClaims({
      claimsId: id,
      method: "PUT",
      data: {
        poNumber: value,
      },
    });
    if (res.data) {
      toast.success(Option_Toast_Message.PO_NUMBER_UPDATE);
    }
  };

  const [mutateClaimsDetails] = useMutateClaimsDetailsMutation();

  const approvedOptionMutate = async ({
    value,
    id,
    statusFor,
    claimsType,
    claimsDetailId,
    successMsg,
  }: TapprovedOption): Promise<void> => {
    if (!value || !id) {
      return;
    }

    const res = await mutateClaimsDetails({
      method: "PUT",
      data: {
        [statusFor]: value,
      },
      claimsId: id,
      claimsType,
      claimsDetailId,
    });

    if (res.data) {
      toast.success(successMsg);
    }
  };

  const updateDetailData = async ({
    data,
    claimsId,
    claimsType,
    claimsDetailId,
    successMsg,
  }: TupdateDetailData): Promise<void> => {
    if (!claimsDetailId || !claimsId) {
      return;
    }

    const res = await mutateClaimsDetails({
      method: "PUT",
      data,
      claimsId,
      claimsType,
      claimsDetailId,
    });

    if (res.data) {
      toast.success(successMsg);
    }
  };

  const mutateClaimsInvoice = async ({
    claimsId,
    claimsType,
    data,
    showToast = true,
    method,
  }: TmutateClaims): Promise<void> => {
    const res = await mutateClaimsDetails({
      method: method.toUpperCase(),
      data,
      claimsId,
      claimsType,
    });
    if (res.data) {
      if (method === "PATCH") {
        toast.success(Option_Toast_Message.INVOICE_GENERATED_SUCCESS);
      } else if (method === "DELETE" && showToast) {
        toast.success(Option_Toast_Message.INVOICE_DELETED_SUCCESS);
      }
    }
  };

  const [mutateEstimate] = useMutateEstimateMutation();

  const handleEstimateMutation = async (
    data: object,
    estimateId: string,
    msg: string,
  ): Promise<void> => {
    if (!estimateId) {
      return;
    }

    const res = await mutateEstimate({
      id: "leadsId",
      data,
      estimateId,
    });

    if (res.data) {
      toast.success(msg);
    }
    dispatch(claimsApi.util.invalidateTags(["drafts"]));
  };

  const [mutateEstimateOptions] = useMutateEstimateOptionsMutation();

  const addEstimationOptions = async (
    data: NewTOptions<AllForOptions>,
  ): Promise<void> => {
    const res = await mutateEstimateOptions({
      id: "leadsId",
      method: "POST",
      data,
    });
    validateResponse({
      response: res,
      onSuccess: () => {
        toast.success(Option_Toast_Message.ADD);
      },
    });
  };
  const editEstimationOptions = async (
    data: TNewEstimateOptions,
  ): Promise<void> => {
    const res = await mutateEstimateOptions({
      id: "leadsId",
      method: "PUT",
      data: data?.data,
    });
    validateResponse({
      response: res,
      onSuccess: () => {
        toast.success(Option_Toast_Message.UPDATE);
      },
    });
  };

  return (
    <taskClaimsContext.Provider
      value={{
        option,
        setOption,
        isSheet,
        setSheet,
        poStatusList: claimsStatus.poStatusList ?? [],
        invoiceMethodList: claimsStatus.invoiceMethodList ?? [],
        statuses: claimsStatus.statuses ?? [],
        submissions: claimsStatus.submissions ?? [],
        estimatedByList,
        customerList,

        deleteOption,
        addOption,
        editOption,
        optionMutate,
        updatePoNumber,
        approvedOptionMutate,
        updateDetailData,
        mutateClaimsInvoice,
        handleEstimateMutation,
        addEstimationOptions,
        editEstimationOptions,
      }}
    >
      {children}
    </taskClaimsContext.Provider>
  );
};

export const useTaskClaimsContext = (): TaskClaimsContextProps => {
  return useContext(taskClaimsContext);
};
